import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ElementType,
} from "react";
import mapboxgl, { Map as MapboxMap } from "mapbox-gl";
import {
  type GeoJsonFeature,
  type GeoJsonFeatureCollection,
} from "@typings/components/map";

import "mapbox-gl/dist/mapbox-gl.css";
import { config } from "@/config";
import CircularProgress from "@mui/material/CircularProgress";
import { usePage } from "@hooks/usePage";
import { applyGeoJsonLayer } from "./MapHelpers";
import useMapMarkerPopup from "@hooks/useMapMarkerPopup";
import { useNavigate } from "react-router-dom";
import SatelliteAltSharpIcon from "@mui/icons-material/SatelliteAltSharp";
import { IconButton } from "@mui/material";

mapboxgl.accessToken = config.mapbox.accessToken;

type MapDisplayProps<T> = {
  longitude?: number;
  latitude?: number;
  zoom?: number;
  style?: string;
  geoJsonData?: GeoJsonFeatureCollection<T> | null;
  popupComponent?: ElementType<{
    feature: GeoJsonFeature<T>;
    onNavigate: (path: string) => void;
  }>;
};

function MapDisplay<T>({
  longitude = config.map?.longitude as number,
  latitude = config.map?.latitude as number,
  zoom = config.map?.zoom as number,
  style = config.map?.style as string,
  popupComponent,
  geoJsonData = null,
}: MapDisplayProps<T>) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const { areaMap } = usePage();
  const navigate = useNavigate();
  const [isSatellite, setIsSatellite] = useState<boolean>(false);

  const areaMapRef = useRef(areaMap);

  useEffect(() => {
    areaMapRef.current = areaMap;
  }, [areaMap]);

  useMapMarkerPopup({
    map: mapLoaded ? mapRef.current : null,
    geoJsonData,
    popupComponent,
    navigate,
  });

  const toggleMapStyle = useCallback(() => {
    if (!mapRef.current) return;

    const newStyle = isSatellite
      ? config.map?.style
      : config.map?.styleSatellite;

    mapRef.current.setStyle(newStyle || style);
    setIsSatellite(!isSatellite);
  }, [isSatellite, style]);

  useEffect(() => {
    if (!mapRef.current) return;

    const handleStyleData = () => {
      if (!mapRef.current) return;

      if (areaMapRef.current) {
        applyGeoJsonLayer(mapRef.current, areaMapRef.current);
      }
    };

    mapRef.current.on("styledata", handleStyleData);

    return () => {
      mapRef.current?.off("styledata", handleStyleData);
    };
  }, [mapLoaded]);

  useEffect(() => {
    if (!mapRef.current || !mapLoaded || !areaMap) return;
    applyGeoJsonLayer(mapRef.current, areaMap);
  }, [mapLoaded, areaMap]);

  useEffect(() => {
    if (!mapContainerRef.current) return;

    setMapLoaded(false);

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style,
      center: [longitude, latitude],
      zoom,
    });

    mapRef.current.on("load", () => setMapLoaded(true));

    return () => mapRef.current?.remove();
  }, [longitude, latitude, zoom, style]);

  return (
    <div
      data-testid="map-container"
      ref={mapContainerRef}
      className="w-full h-full"
    >
      <div className="absolute top-2 right-2 z-10">
        <IconButton
          onClick={toggleMapStyle}
          className="bg-white shadow-lg hover:bg-gray-100"
          aria-label="Alternar visão satélite"
        >
          <SatelliteAltSharpIcon
            className={`${isSatellite ? "text-blue-500" : "text-gray-600"} text-[18px]`}
          />
        </IconButton>
      </div>
      {!mapLoaded && (
        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-white bg-opacity-70 z-10">
          <CircularProgress className="text-primary" />
        </div>
      )}
    </div>
  );
}

export default React.memo(MapDisplay) as typeof MapDisplay;
